/* KANON `Programy v2 - D3 (0.8)` — the anatomy both list rows share, transcribed once.
 *
 * The specimen builds its two rows from one `commonD()` and one `rowDSt()`: the same grid, the same
 * 9.5px gap, the same 11px inset, the same hairline, the same `--bg-inset` fill and 2px bar when
 * selected, the same cover under a 2.5px category rule, the same name-over-sub-line column, the
 * same 13px mode mark beside its label. What the two rows differ by is a LEADING AVATAR COLUMN and
 * a HEIGHT — 73px for a template, 86px for a client's plan — plus what each one puts in its last
 * track.
 *
 * ── The row is a FIFTH taller than the canon draws it, and its contents grew with it ─────────
 * Owner decision **D28**, 4 September 2026: the canon's 61 / 72 read as cramped, so they are 73 and
 * 86 here. A taller box on its own would be a short row with air above and below it, so the things
 * inside it are re-cut on the same fifth rather than left at the specimen's size: the cover is
 * 61 x 48 against the canon's 51 x 40, the client-plan row's disc is 38 against 32, the micro line
 * is `--fs-pl-row` at 10px against the specimen's 9, and the name-to-sub-line gap is 4px against 3.
 * Nothing here is "pinned" — the grid has always been `align-items: center` — what changed is that
 * the contents are now sized for the box they stand in.
 *
 * So the shared half lives here and each row's own file carries only its differences. Two
 * stylesheets repeating fifteen identical blocks is precisely the near-duplicate the value
 * discipline exists to prevent, and it is what wave 1 would have grown into the moment wave 3 drew
 * a second row.
 *
 * **Nothing is declared on both sides.** A duplicated declaration between two CSS Modules is
 * settled by chunk order, which differs between the dev server and the build — this repository has
 * been bitten by that once already. A row wears `listRow.row` PLUS its own class, and the two never
 * write the same property.
 *
 * **Every text leaf states its line-height.** `base.css` puts `--lh-relaxed` on `body` and the
 * specimen's body carries none, so an unstated leaf renders about 2px taller than the canon and the
 * whole row drifts.
 *
 * Heights and widths are layout rather than design tokens (see stylelint.config.js); every colour,
 * space, radius and type value below is a token. */

.row {
  position: relative;
  display: grid;
  gap: var(--space-9-5px);
  align-items: center;
  padding: var(--space-0) var(--space-11px);
  border-bottom: var(--border-hairline) solid var(--border);
}

/* The selection is a FILL plus a bar, which is the canon's own pair. The fill alone is `--bg-inset`
 * over `--bg-card` — 1.06:1 in Flame Dark, which is a surface difference rather than a mark — so
 * the bar is what actually says which row the inspector is about. */
.rowSelected {
  background: var(--bg-inset);
}

/* Absolutely positioned so it takes no grid track: the row's content, the toolbar above it and the
 * pane's own inset stay on one left edge. Decorative — the inspector beside it carries the selected
 * plan's name as its own heading, which is what says the same thing in words. */
.selectionBar {
  position: absolute;
  top: 0;
  left: 0;
  width: 2px;
  height: 100%;
  background: var(--text);
}

/* The whole row is ONE click target and ONE tab stop: `position: static` on the anchor with an
 * absolutely positioned `::after` against the row's containing block. One dispatch, and the
 * keyboard path, the focus ring, the status-bar preview and middle-click all come free. */
.rowLink {
  display: block;
  min-width: 0;
  color: inherit;
  text-decoration: none;
  outline: none;
}

.rowLink::after {
  content: '';
  position: absolute;
  inset: var(--space-0);
}

/* The ring is drawn on the ROW, because the link is stretched across the whole of it and an outline
 * on the anchor would trace the overlay rather than the content. */
.row:has(.rowLink:focus-visible) {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--outline-inset);
}

/* ── The cover ──────────────────────────────────────────────────────────────────────────────── */

/* The canon's 6.5px radius belongs to a 51 x 40 crop; at 61 x 48 (D28) the same corner reads
 * tighter than the specimen's does, so it steps to the app's own 8px. One step, not a ratio. */
.cover {
  position: relative;
  display: block;
  width: var(--w-pl-cover);
  height: var(--h-pl-cover);
  overflow: hidden;
  border-radius: var(--radius-8px);
}

/* Geometry only. The fit and the neutral ground are `shared/ui/photoBox`'s `.photo`, applied as a
 * second class in the TSX — see `shared/AGENTS.md`, "A photo holds its own box". */
.coverImage {
  display: block;
  width: var(--w-pl-cover);
  height: var(--h-pl-cover);
}

/* There was a `.coverRule` here: the canon's 2.5px category stripe across the cover's foot, in
 * `--cat-ink`. The owner cut it on 17 September 2026 — with the category already named in words on
 * the row, the stripe was a second, quieter statement of the same fact, and a coloured edge under
 * every thumbnail made the list read as a colour chart. Deviation D10 recorded the hue this app
 * painted it in; it is now recorded as withdrawn. The cover keeps `position: relative` for the
 * selection state that still stands over it.
 *
 * `.cover` no longer needs the category locals set on it in `TemplateRow`; `ClientPlanRow` still
 * sets them on the ROW, where its progress bar reads them. */

/* ── The title and its sub-line ─────────────────────────────────────────────────────────────── */

.identity {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  justify-content: center;
  min-width: 0;
}

/* The ellipsis lives on the LINE, not on the anchor: an `overflow: hidden` anchor would clip its
 * own stretched `::after` and the row would stop being one click target. */
.nameLine {
  overflow: hidden;
  font-size: var(--fs-pl-name);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-display);
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sub {
  overflow: hidden;
  font-size: var(--fs-pl-row);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-3);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── The mode mark and its label ────────────────────────────────────────────────────────────── */

.mode {
  display: inline-flex;
  flex: none;
  gap: var(--space-5px);
  align-items: center;
  color: var(--text-2);
}

.modeIcon {
  display: block;
  flex: none;
  width: 13px;
  height: 13px;
}

.modeLabel {
  font-size: var(--fs-pl-row);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  white-space: nowrap;
}
